import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Select, Space, message } from 'antd';
import { agentApi, agentDepotApi, type AgentDTO, type AgentDepotDTO } from '../api/kfc';

const AgentDepotList: React.FC = () => {
  const [agents, setAgents] = useState<AgentDTO[]>([]);
  const [depots, setDepots] = useState<AgentDepotDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [syncingId, setSyncingId] = useState<string | null>(null);
  const [filterAgentIds, setFilterAgentIds] = useState<string[]>([]);
  const [syncAgentId, setSyncAgentId] = useState<string | null>(null);

  const loadAgents = () => {
    agentApi.list().then((res) => setAgents(res.data)).catch(() => setAgents([]));
  };

  const loadDepots = (agentId?: string) => {
    setLoading(true);
    agentDepotApi
      .list(agentId)
      .then((res) => setDepots(res.data))
      .catch(() => setDepots([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadAgents();
  }, []);

  useEffect(() => {
    const agentId = filterAgentIds?.length === 1 ? filterAgentIds[0] : undefined;
    loadDepots(agentId);
  }, [filterAgentIds]);

  const sync = (agentId: string) => {
    setSyncingId(agentId);
    agentDepotApi
      .syncFromAgent(agentId)
      .then((res) => {
        message.success(res.data.success ? res.data.message : (res.data.message || '同步失败'));
        if (res.data.success) loadDepots(filterAgentIds?.length === 1 ? filterAgentIds[0] : undefined);
      })
      .catch((e) => message.error(e.response?.data?.message || '同步失败'))
      .finally(() => setSyncingId(null));
  };

  const filteredDepots =
    filterAgentIds?.length > 0
      ? depots.filter((d) => d.agentId && filterAgentIds.includes(d.agentId))
      : depots;

  return (
    <>
      <Card
        title="代理仓库列表"
        extra={
          <Space>
            <Select
              mode="multiple"
              placeholder="按代理名称或ID筛选"
              allowClear
              style={{ minWidth: 260 }}
              maxTagCount="responsive"
              value={filterAgentIds}
              onChange={setFilterAgentIds}
              options={agents.map((a) => ({
                value: a.id!,
                label: `${a.basicInfo?.name ?? ''} (${a.basicInfo?.identifier ?? a.id})`,
              }))}
              filterOption={(input, opt) =>
                (opt?.label?.toString() ?? '').toLowerCase().includes(input.toLowerCase())
              }
            />
            <Select
              placeholder="选择代理"
              allowClear
              style={{ minWidth: 180 }}
              value={syncAgentId}
              onChange={setSyncAgentId}
              options={agents.map((a) => ({
                value: a.id!,
                label: a.basicInfo?.name ?? a.id,
              }))}
            />
            <Button
              type="primary"
              onClick={() => syncAgentId && sync(syncAgentId)}
              loading={syncingId !== null}
              disabled={!syncAgentId}
            >
              同步仓库
            </Button>
          </Space>
        }
      >
        <p style={{ marginBottom: 16 }}>
          操作区：按代理筛选仓库列表；选择代理后点击「同步」将调用代理的获取仓库列表 API 并更新本地数据。
        </p>
        <Table
          loading={loading}
          dataSource={filteredDepots}
          rowKey={(r) => r.id ?? `${r.agentId}-${r.depotId}`}
          scroll={{ x: 700 }}
          columns={[
            { title: '代理ID', dataIndex: 'agentId', width: 120, ellipsis: true },
            { title: '仓库ID', dataIndex: 'depotId', width: 100, ellipsis: true },
            { title: '仓库名称', dataIndex: 'depotName', width: 140, ellipsis: true },
            { title: '仓库地址', dataIndex: 'depotAddr', width: 200, ellipsis: true },
            { title: '仓库联系人/联系方式', dataIndex: 'depotContact', width: 160, ellipsis: true },
          ]}
          pagination={{ pageSize: 20 }}
        />
      </Card>
    </>
  );
};

export default AgentDepotList;
